/**
 * Formulário de Edição de URA (Unidade de Resposta Audível)
 * VigosCloud - URA System
 * 
 * Permite editar URA existente
 */

import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { accessLocalStorage, exportSelectAllQueues, getContextsByTenant, getIntegrationsByAccountcode } from '../../../services';
import * as URAApi from '../../../services/uraApi';

import Loading from '../../Loading/LoadingModule';
import MenuConfiguration from './components/MenuConfiguration';
import ExtensionManager from './components/ExtensionManager';
import MohConfiguration from '../shared/MohConfiguration';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSave,
  faArrowLeft,
  faPhoneVolume,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import '../../../libs/bulma.min.css';
import './URAForm.css';

function URAEdit() {
  const history = useHistory();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState('');
  const [hasWhatsAppIntegration, setHasWhatsAppIntegration] = useState(false);
  const [availableQueues, setAvailableQueues] = useState([]);
  const [availableContexts, setAvailableContexts] = useState([]);
  
  // Estado do formulário
  const [values, setValues] = useState({
    tenant: '',
    context_name: '',
    description: '',
    
    // Menu Principal
    menu_extension: 'menu',
    menu_audio_enabled: false,
    menu_audio_file: '',
    menu_audio_type: '',
    menu_audio_accountcode: '',
    menu_audio_method: 'background',
    menu_gotoiftime_rules: [],
    
    // WaitExten
    waitexten_timeout: 10,
    waitexten_destination_type: 'hangup',
    waitexten_destination: '',
    waitexten_overflows: [],
    
    // Ciclo
    cycle_enabled: false,
    max_retries: 3,
    
    // Music on Hold
    moh_enabled: false,
    moh_class: '',
    
    // Extensões
    extensions: []
  });
  
  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const tenant = user.user.idtenant;
      setUserTenant(tenant);
      setUserRole(user.user.role || '');

      // Buscar filas disponíveis
      try {
        console.log('🔍 [URA-EDIT] Buscando filas...');
        const queuesData = await exportSelectAllQueues();
        console.log('📦 [URA-EDIT] Dados de filas recebidos:', queuesData);
        
        let queues = [];
        if (Array.isArray(queuesData)) {
          queues = queuesData;
        } else if (queuesData && Array.isArray(queuesData.data)) {
          queues = queuesData.data;
        } else if (queuesData && queuesData.queues) {
          queues = queuesData.queues;
        }
        
        console.log('📋 [URA-EDIT] Exemplo de fila:', queues[0]);
        
        // Filtrar filas por tenant (usar accountcodequeue como no IncomingCallsCreate)
        const tenantQueues = tenant.includes('vgs') 
          ? queues 
          : queues.filter(q => {
              const accountcode = q.accountcodequeue || q.accountcode;
              return tenant.includes(accountcode);
            });
        
        setAvailableQueues(tenantQueues);
        console.log(`✅ [URA-EDIT] ${tenantQueues.length} filas carregadas:`, tenantQueues);
      } catch (error) {
        console.error('⚠️ [URA-EDIT] Erro ao buscar filas:', error);
      }
      
      // Buscar contextos disponíveis (para DISA)
      try {
        console.log('🔍 [URA-EDIT] Buscando contextos...');
        const contextsData = await getContextsByTenant(tenant);
        console.log('📦 [URA-EDIT] Dados de contextos recebidos:', contextsData);
        
        let contexts = [];
        if (Array.isArray(contextsData)) {
          contexts = contextsData;
        } else if (contextsData && Array.isArray(contextsData.data)) {
          contexts = contextsData.data;
        } else if (contextsData && contextsData.contexts) {
          contexts = contextsData.contexts;
        }
        
        setAvailableContexts(contexts);
        console.log(`✅ [URA-EDIT] ${contexts.length} contextos carregados`);
      } catch (error) {
        console.error('⚠️ [URA-EDIT] Erro ao buscar contextos:', error);
      }
      
      // Verificar integração WhatsApp
      await checkWhatsAppIntegration(tenant);
      
      console.log(`🔍 [URA-EDIT] Carregando URA ID: ${id}`);
      
      const response = await URAApi.getURAConfigById(id);
      
      if (response.success) {
        const uraData = response.data;
        
        console.log('✅ [URA-EDIT] URA carregada:', uraData);
        
        // Processar waitexten_destination: separar trunk e number se for external
        let waitextenExternalTrunk = '';
        let waitextenExternalNumber = '';
        
        if (uraData.waitexten_destination_type === 'external' && uraData.waitexten_destination) {
          if (uraData.waitexten_destination.includes('/')) {
            const [trunk, number] = uraData.waitexten_destination.split('/');
            waitextenExternalTrunk = trunk;
            waitextenExternalNumber = number;
          } else if (uraData.waitexten_destination.includes('@')) {
            const [number, trunk] = uraData.waitexten_destination.split('@');
            waitextenExternalTrunk = trunk;
            waitextenExternalNumber = number;
          }
        }
        
        // Processar waitexten_overflows: separar trunk e number nos destinos externos
        const processedWaitextenOverflows = (uraData.waitexten_overflows || []).map(overflow => {
          const processedOverflow = { ...overflow };
          
          if (overflow.destination_type === 'external' && overflow.destination_overflow) {
            if (overflow.destination_overflow.includes('/')) {
              const [trunk, number] = overflow.destination_overflow.split('/');
              processedOverflow.overflow_external_trunk = trunk;
              processedOverflow.overflow_external_number = number;
            } else if (overflow.destination_overflow.includes('@')) {
              const [number, trunk] = overflow.destination_overflow.split('@');
              processedOverflow.overflow_external_trunk = trunk;
              processedOverflow.overflow_external_number = number;
            }
          }
          
          return processedOverflow;
        });
        
        // Processar extensões: separar trunk e number nos destinos externos
        const processedExtensions = (uraData.extensions || []).map(ext => {
          const processed = {
            ...ext,
            gotoiftime_rules: ext.gotoiftime_rules || []
          };
          
          // Separar trunk e number do destino principal
          if (ext.destination_type_main === 'external' && ext.destination_main) {
            if (ext.destination_main.includes('/')) {
              const [trunk, number] = ext.destination_main.split('/');
              processed.external_trunk = trunk;
              processed.external_number = number;
            } else if (ext.destination_main.includes('@')) {
              const [number, trunk] = ext.destination_main.split('@');
              processed.external_trunk = trunk;
              processed.external_number = number;
            }
          }
          
          // Separar trunk e number dos overflows
          if (processed.overflows && Array.isArray(processed.overflows)) {
            processed.overflows = processed.overflows.map(overflow => {
              const processedOverflow = { ...overflow };
              
              if (overflow.destination_type === 'external' && overflow.destination_overflow) {
                if (overflow.destination_overflow.includes('/')) {
                  const [trunk, number] = overflow.destination_overflow.split('/');
                  processedOverflow.overflow_external_trunk = trunk;
                  processedOverflow.overflow_external_number = number;
                } else if (overflow.destination_overflow.includes('@')) {
                  const [number, trunk] = overflow.destination_overflow.split('@');
                  processedOverflow.overflow_external_trunk = trunk;
                  processedOverflow.overflow_external_number = number;
                }
              }
              
              return processedOverflow;
            });
          }
          
          return processed;
        });
        
        setValues({
          tenant: uraData.tenant,
          context_name: uraData.context_name,
          description: uraData.description || '',
          
          menu_extension: uraData.menu_extension || 'menu',
          menu_audio_enabled: uraData.menu_audio_enabled || false,
          menu_audio_file: uraData.menu_audio_file || '',
          menu_audio_type: uraData.menu_audio_type || '',
          menu_audio_accountcode: uraData.menu_audio_accountcode || tenant,
          menu_audio_method: uraData.menu_audio_method || 'background',
          menu_gotoiftime_rules: uraData.menu_gotoiftime_rules || [],
          
          waitexten_timeout: uraData.waitexten_timeout || 10,
          waitexten_destination_type: uraData.waitexten_destination_type || 'hangup',
          waitexten_destination: uraData.waitexten_destination || '',
          waitexten_external_trunk: waitextenExternalTrunk,
          waitexten_external_number: waitextenExternalNumber,
          waitexten_overflows: processedWaitextenOverflows,
          
          cycle_enabled: uraData.cycle_enabled || false,
          max_retries: uraData.max_retries || 3,
          
          moh_enabled: uraData.moh_enabled === true || uraData.moh_enabled === 't' || uraData.moh_enabled === 'true',
          moh_class: uraData.moh_class || '',
          
          extensions: processedExtensions
        });
      } else {
        console.error('❌ [URA-EDIT] Erro ao carregar URA:', response.error);
        showAlert('danger', response.error || 'Erro ao carregar URA');
        setTimeout(() => history.push('/config/ura'), 2000);
      }
      
    } catch (error) {
      console.error('❌ [URA-EDIT] Erro ao carregar dados:', error);
      showAlert('danger', 'Erro ao carregar URA');
    } finally {
      setLoading(false);
    }
  };

  // Função para verificar se há integrações WhatsApp cadastradas
  const checkWhatsAppIntegration = async (tenant) => {
    try {
      console.log('🔍 [URA-EDIT] Verificando integrações WhatsApp para tenant:', tenant);
      
      const response = await getIntegrationsByAccountcode(tenant);
      
      if (response && response.success && response.data && response.data.length > 0) {
        setHasWhatsAppIntegration(true);
        console.log('✅ [URA-EDIT] Integrações WhatsApp encontradas:', response.data.length);
      } else {
        setHasWhatsAppIntegration(false);
        console.log('⚠️ [URA-EDIT] Nenhuma integração WhatsApp encontrada');
      }
    } catch (error) {
      console.error('❌ [URA-EDIT] Erro ao verificar integrações WhatsApp:', error);
      setHasWhatsAppIntegration(false);
    }
  };

  const handleChange = (field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
  };

  const handleMenuChange = (menuConfig) => {
    setValues(prev => ({ ...prev, ...menuConfig }));
  };

  const handleExtensionsChange = (newExtensions) => {
    setValues(prev => ({ ...prev, extensions: newExtensions }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔍 [URA-EDIT] Dados a serem enviados:', values);
    
    // Validação frontend
    const validation = URAApi.validateURAConfig(values);
    if (!validation.isValid) {
      // Exibir todos os erros de validação
      const errorMessage = validation.errors.length === 1 
        ? validation.errors[0]
        : `Corrija os seguintes erros:\n${validation.errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
      
      showAlert('danger', errorMessage);
      console.error('❌ [URA-EDIT] Validação falhou:', validation.errors);
      return;
    }
    
    try {
      setLoading(true);
      
      // Formatar dados para API
      const formattedData = URAApi.formatConfigDataForAPI(values);
      
      console.log('📤 [URA-EDIT] Enviando para API:', formattedData);
      
      const response = await URAApi.updateURAConfig(id, formattedData);
      
      if (response.success) {
        console.log('✅ [URA-EDIT] URA atualizada com sucesso:', response.data);
        showAlert('success', 'URA atualizada com sucesso!');
        
        setTimeout(() => {
          history.push('/config/ura');
        }, 2000);
      } else {
        console.error('❌ [URA-EDIT] Erro ao atualizar URA:', response);
        showAlert('danger', response.error || 'Erro ao atualizar URA');
        
        if (response.details && Array.isArray(response.details)) {
          response.details.forEach(detail => {
            console.error('  -', detail.msg || detail);
          });
        }
      }
      
    } catch (error) {
      console.error('❌ [URA-EDIT] Erro ao atualizar URA:', error);
      showAlert('danger', 'Erro ao atualizar URA');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    // Scroll para o topo da página para mostrar o alert
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 5000);
  };

  const handleCancel = () => {
    history.push('/config/ura');
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="ura-form-container">
      <div className="ura-form-header">
        <div className="ura-form-title-wrapper">
          <svg className="ura-form-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
          </svg>
          <div>
            <h1 className="ura-form-title">Editar URA: {values.context_name}</h1>
            <p className="ura-form-subtitle">Modifique a configuração da URA</p>
          </div>
        </div>
        <button className="btn-back" onClick={handleCancel}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </button>
      </div>

      {alert.show && (
        <div className="alerts-section">
          <div className={`alert-message alert-${alert.type}`}>
            <div className="alert-header">
              <span>{alert.type === 'success' ? 'Sucesso!' : alert.type === 'danger' ? 'Erro!' : 'Atenção!'}</span>
              <button className="alert-close" onClick={() => setAlert({ ...alert, show: false })}>×</button>
            </div>
            <div className="alert-body">{alert.message}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-section-card">
          <h3 className="section-title">
            <FontAwesomeIcon icon={faInfoCircle} className="section-icon" />
            Informações Básicas
          </h3>
          
          <div className="form-field">
            <label className="form-label">Tenant</label>
            <input
              className="form-input"
              type="text"
              value={values.tenant}
              disabled
            />
          </div>
          
          <div className="form-field">
            <label className="form-label">Nome do Contexto *</label>
            <input
              className="form-input"
              type="text"
              value={values.context_name}
              disabled
            />
            <p className="form-help">O nome do contexto não pode ser alterado após criação</p>
          </div>
          
          <div className="form-field">
            <label className="form-label">Descrição</label>
            <textarea
              className="form-textarea"
              placeholder="Ex: Menu principal de atendimento"
              value={values.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows="2"
            />
          </div>
        </div>

        <MenuConfiguration
          config={values}
          onChange={handleMenuChange}
          tenant={userTenant}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
        />

        {/* Configuração de Music on Hold */}
        <div className="form-section-card">
          <MohConfiguration
            config={{
              moh_enabled: values.moh_enabled || false,
              moh_class: values.moh_class || ''
            }}
            tenant={userTenant}
            onChange={(mohConfig) => {
              setValues(prev => ({
                ...prev,
                moh_enabled: mohConfig.moh_enabled,
                moh_class: mohConfig.moh_class
              }));
            }}
            disabled={loading}
          />
        </div>

        <ExtensionManager
          extensions={values.extensions}
          onChange={handleExtensionsChange}
          tenant={userTenant}
          hasWhatsAppIntegration={hasWhatsAppIntegration}
          availableQueues={availableQueues}
          availableContexts={availableContexts}
          userRole={userRole}
        />

        <div className="form-section-card">
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>Salvando...</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  Salvar Configuração
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default URAEdit;

